/**
 * Application shell: header, footer, theme handling, ad slots.
 */

import { useEffect, useState, type ReactNode } from 'react'
import { Link, NavLink } from 'react-router-dom'
import { FileText, Mail, MessageSquareWarning, Moon, Share2, Sun } from 'lucide-react'
import { SITE } from '@/config/site'
import { applyTheme, useUiStore } from '@/store/uiStore'
import { adsConfigured, hasAdConsent, loadAdSense, pushAdSlot } from '@/lib/ads'
import { useShareStore } from '@/store/shareStore'
import { HOMEPAGE_SHARE_RESOURCE } from '@/lib/share'
import { LinkedInIcon, PinterestIcon, XIcon } from '@/components/share/BrandIcons'
import { ShareModal } from '@/components/share/ShareModal'
import { SharePromptModal } from '@/components/share/SharePromptModal'
import { ConsentBanner, CookieChoicesButton } from './ConsentBanner'
import { MobileMenu } from './MobileMenu'
import { NotificationBell } from './NotificationBell'

/*
 * `How it works` leads, because it is the only entry aimed at someone who does
 * not yet know what the other five do. The rest stay in their existing order —
 * this adds an item, it does not re-rank the tools.
 *
 * Seven items is the ceiling for this header: measured at the `lg` breakpoint
 * (1024px) the row fits with room to spare, but an eighth would collide with the
 * theme controls and the CTA. The next addition belongs in the footer or the
 * mobile menu, not here.
 */
const NAV = [
  { to: '/how-it-works', label: 'How it works' },
  { to: '/resume-builder', label: 'Builder' },
  { to: '/ats-resume-checker', label: 'ATS Checker' },
  { to: '/job-description-matcher', label: 'Job Matcher' },
  { to: '/resume-bullet-improver', label: 'Bullet Improver' },
  { to: '/guides', label: 'Guides' },
]

/** Footer social row. `mailto:` opens the user's mail client, so it skips `target`/`rel`. */
const SOCIAL_LINKS = [
  { name: 'LinkedIn', href: 'https://www.linkedin.com/in/still-figuring-it-out', Icon: LinkedInIcon },
  { name: 'Pinterest', href: 'https://in.pinterest.com/thelifecatalyst2025/', Icon: PinterestIcon },
  { name: 'X (Twitter)', href: 'https://x.com/StillFigureX', Icon: XIcon },
  { name: 'Email', href: 'mailto:thelifecatalyst2025@gmail.com', Icon: Mail },
]

export const ThemeController = () => {
  const theme = useUiStore((s) => s.theme)
  const highContrast = useUiStore((s) => s.highContrast)
  const setTheme = useUiStore((s) => s.setTheme)
  const openShare = useShareStore((s) => s.openShare)

  useEffect(() => {
    applyTheme(theme, highContrast)
    if (theme !== 'system') return
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => applyTheme('system', highContrast)
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [theme, highContrast])

  const resolved = theme === 'system' ? 'system' : theme

  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        className="btn btn-ghost btn-sm btn-square"
        onClick={() => setTheme(resolved === 'dark' ? 'light' : 'dark')}
        aria-label={`Switch to ${resolved === 'dark' ? 'light' : 'dark'} theme`}
        title="Toggle dark mode"
      >
        {resolved === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
      </button>
      {/*
        Replaces the previous high-contrast toggle in this slot. The
        high-contrast theme itself (`highContrast` in uiStore, the
        atslight-hc/atsdark-hc daisyUI themes) is untouched — this only
        removes its header entry point, so a visitor who had it on from
        before keeps seeing it; there's just no header control to turn it
        on/off anymore.
      */}
      <button
        type="button"
        className="btn btn-ghost btn-sm btn-square"
        onClick={() => openShare(HOMEPAGE_SHARE_RESOURCE, 'header')}
        aria-label="Share this resource"
        title="Share"
      >
        <Share2 size={18} />
      </button>
    </div>
  )
}

const Header = () => (
  /* `relative` so the mobile menu can anchor to the header rather than the page. */
  <header className="no-print sticky top-0 z-40 border-b border-base-300 bg-base-100/95 backdrop-blur relative">
    {/* Fixed height so the builder's `100dvh - header` calc is provably correct
        rather than a guess that leaves a dead band above the footer. */}
    <div className="mx-auto flex h-(--app-header-h) max-w-7xl items-center gap-1 px-3 sm:gap-2 sm:px-4">
      {/*
        aria-label, not just the visible span: the site name text is `hidden`
        below `sm` (icon-only on mobile), so without it this link has no
        accessible name at all on a phone-width viewport — the icon is
        aria-hidden and there is nothing else for a screen reader to read.
      */}
      <Link
        to="/"
        aria-label={SITE.name}
        className="flex shrink-0 items-center gap-2 text-lg font-bold tracking-tight"
      >
        <FileText size={22} className="text-primary" aria-hidden="true" />
        <span className="hidden sm:inline">{SITE.name}</span>
      </Link>

      {/*
        Desktop navigation. Sized up from 14px/32px to 15px/44px: 32px cleared
        the 24px AA minimum but not the 44px AAA target the rest of this product
        holds itself to, and the header is the one control every visitor uses.
      */}
      <nav aria-label="Main" className="ml-3 hidden flex-1 items-center gap-0.5 lg:flex">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              `flex min-h-11 items-center rounded-field px-3 text-[15px] font-medium transition-colors ${
                isActive ? 'bg-primary/10 text-primary' : 'hover:bg-base-200'
              }`
            }
          >
            {item.label}
          </NavLink>
        ))}
      </nav>

      {/*
        `gap-1` → `gap-0.5` below `sm`: measured (iframe-viewport test, not
        estimated) a genuine 10px horizontal overflow at 375px CSS width once
        the notification bell joined this row as a 5th icon — `header.scrollWidth`
        (366px) exceeded `header.clientWidth` (356px), which is real horizontal
        page scroll on a real phone, not a cosmetic nit.
      */}
      <div className="ml-auto flex shrink-0 items-center gap-0.5 sm:gap-1">
        <NotificationBell />
        {/* The "Also built by us" dropdown (PromoMenu) was removed on
            2026-10-21 at the owner's request, together with the footer's
            "More free tools" list. */}
        <ThemeController />
        <Link to="/resume-builder" className="btn btn-primary h-11 min-h-11 px-3 text-[15px] sm:px-4">
          Build resume
        </Link>
        <MobileMenu items={NAV} />
      </div>
    </div>
  </header>
)

const Footer = () => {
  const openShare = useShareStore((s) => s.openShare)

  return (
  <footer className="no-print mt-12 border-t border-base-300 bg-base-200">
    <div className="mx-auto grid max-w-7xl gap-8 px-4 py-10 sm:grid-cols-2 lg:grid-cols-4">
      <div>
        <p className="flex items-center gap-2 font-bold">
          <FileText size={18} className="text-primary" aria-hidden="true" />
          {SITE.name}
        </p>
        <p className="mt-2 text-sm opacity-70">
          Free to use in full — no account, no trial, nothing held back for a paid plan. Your
          resume never leaves your browser.
        </p>

        <p className="mt-4 text-xs font-semibold opacity-60">Connect</p>
        <div className="mt-1 flex items-center gap-1">
          {SOCIAL_LINKS.map(({ name, href, Icon }) => (
            <a
              key={name}
              href={href}
              target={href.startsWith('mailto:') ? undefined : '_blank'}
              rel={href.startsWith('mailto:') ? undefined : 'noopener noreferrer'}
              className="btn btn-ghost btn-sm btn-square"
              aria-label={name}
              title={name}
            >
              <Icon size={18} />
            </a>
          ))}
        </div>
      </div>

      <div>
        <h2 className="mb-2 text-sm font-semibold">Tools</h2>
        <ul className="space-y-1 text-sm">
          <li><Link className="link link-hover inline-flex min-h-9 items-center" to="/ats-resume-checker">ATS Resume Checker</Link></li>
          <li><Link className="link link-hover inline-flex min-h-9 items-center" to="/job-description-matcher">Job Description Matcher</Link></li>
          <li><Link className="link link-hover inline-flex min-h-9 items-center" to="/resume-bullet-improver">Bullet Point Improver</Link></li>
          <li><Link className="link link-hover inline-flex min-h-9 items-center" to="/resume-builder">Resume Builder</Link></li>
          <li><Link className="link link-hover inline-flex min-h-9 items-center" to="/tools">All tools</Link></li>
          <li><Link className="link link-hover inline-flex min-h-9 items-center" to="/how-it-works">How it works</Link></li>
          <li><Link className="link link-hover inline-flex min-h-9 items-center" to="/faq">FAQ</Link></li>
        </ul>
      </div>

      <div>
        <h2 className="mb-2 text-sm font-semibold">Guides</h2>
        <ul className="space-y-1 text-sm">
          <li><Link className="link link-hover inline-flex min-h-9 items-center" to="/guides/ats-friendly-resume">How an ATS reads a resume</Link></li>
          <li><Link className="link link-hover inline-flex min-h-9 items-center" to="/guides/ats-resume-format">ATS resume format</Link></li>
          <li><Link className="link link-hover inline-flex min-h-9 items-center" to="/guides/resume-bullet-points-examples">Writing bullet points</Link></li>
          <li><Link className="link link-hover inline-flex min-h-9 items-center" to="/guides/resume-tailoring-to-job-description">Tailoring to a job</Link></li>
          <li><Link className="link link-hover inline-flex min-h-9 items-center" to="/guides">All guides</Link></li>
        </ul>
      </div>

      <div>
        <h2 className="mb-2 text-sm font-semibold">How it works</h2>
        <ul className="space-y-1 text-sm">
          <li><Link className="link link-hover inline-flex min-h-9 items-center" to="/methodology">Methodology</Link></li>
          <li><Link className="link link-hover inline-flex min-h-9 items-center" to="/evidence">Parsing experiments</Link></li>
          <li><Link className="link link-hover inline-flex min-h-9 items-center" to="/about">About</Link></li>
          <li><Link className="link link-hover inline-flex min-h-9 items-center" to="/contact">Contact</Link></li>
        </ul>
      </div>
    </div>

    <div className="border-t border-base-300 px-4 py-4">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 text-sm">
        <p className="opacity-70">
          © {new Date().getFullYear()} {SITE.name}. Free to use — every tool, every template,
          every export.
        </p>
        {/*
          `flex-wrap` was missing here — six items (two of them icon+multi-word
          text: "Contact", "Share ATSResumeKit") were forced onto one
          unbroken line with no wrap, which is what actually overflowed on a
          phone; the outer row above already wraps, but that only lets *this
          nav* drop to its own line, not its own children wrap within it.
        */}
        <nav aria-label="Legal" className="flex flex-wrap gap-x-4 gap-y-2">
          <Link className="link link-hover inline-flex min-h-9 items-center" to="/privacy">Privacy</Link>
          {/* Reopens the consent banner from any page, the builder included (2026-10-21). */}
          <CookieChoicesButton className="link link-hover inline-flex min-h-9 items-center" />
          <Link className="link link-hover inline-flex min-h-9 items-center" to="/terms">Terms</Link>
          <Link className="link link-hover inline-flex min-h-9 items-center" to="/disclaimer">Disclaimer</Link>
          <Link className="link link-hover inline-flex min-h-9 items-center" to="/about">About</Link>
          <Link className="link link-hover inline-flex min-h-9 items-center" to="/methodology">Methodology</Link>
          <Link className="link link-hover inline-flex min-h-9 items-center" to="/evidence">Evidence</Link>
          {/*
            No "Source" link and no issue-tracker link. Both used to live here
            and both required an account elsewhere before anyone could say
            anything. `/contact` needs neither.
          */}
          <Link className="link link-hover inline-flex min-h-9 items-center gap-1" to="/contact">
            <MessageSquareWarning size={14} aria-hidden="true" /> Contact
          </Link>
          <button
            type="button"
            className="link link-hover inline-flex min-h-9 items-center gap-1"
            onClick={() => openShare(HOMEPAGE_SHARE_RESOURCE, 'footer')}
          >
            <Share2 size={14} aria-hidden="true" /> Share ATSResumeKit
          </button>
        </nav>
      </div>
    </div>
  </footer>
  )
}

/**
 * Ad slot.
 *
 * Renders nothing at all unless a publisher ID is configured, so the default
 * clean deployment ships zero ad markup and zero third-party requests.
 * Never rendered inside the builder — editing must not be interrupted, which is
 * a product rule, not a styling preference.
 */
export const AdSlot = ({ slot, className = '' }: { slot: string; className?: string }) => {
  const [ready, setReady] = useState(false)

  /*
   * The container previously rendered an `<ins>` and nothing else — no script
   * was ever loaded and no fill was ever requested, so with a publisher id set
   * it would have produced empty boxes rather than ads. Loading is now driven
   * from here, after consent, and re-checked when the consent event fires.
   */
  useEffect(() => {
    const sync = () => {
      if (!hasAdConsent()) {
        setReady(false)
        return
      }
      loadAdSense()
        .then(() => {
          setReady(true)
          pushAdSlot()
        })
        .catch(() => setReady(false))
    }
    sync()
    window.addEventListener('ats:consent', sync)
    return () => window.removeEventListener('ats:consent', sync)
  }, [])

  // Nothing configured, or consent not given: render no markup and no space.
  if (!adsConfigured() || !ready) return null

  return (
    <aside
      className={`no-print my-8 flex min-h-22.5 items-center justify-center rounded-box border border-dashed border-base-300 ${className}`}
      /* Google requires ads to be distinguishable from content. The visible
         label does that for sighted users; aria-label does it for the rest. */
      aria-label="Advertisement"
    >
      <span className="sr-only">Advertisement</span>
      <ins
        className="adsbygoogle block w-full"
        data-ad-client={SITE.adsenseClientId}
        data-ad-slot={slot}
        data-ad-format="auto"
        data-full-width-responsive="true"
      />
    </aside>
  )
}

/** Sponsor placement. Static markup, no third-party script, no tracking. */
export const SponsorSlot = () => (
  <aside className="no-print my-8 rounded-box border border-base-300 bg-base-200 p-4 text-center text-sm">
    {/*
      `text-base-content/70`, not `opacity-70`. Element opacity composites the
      whole subtree, so the primary-coloured link inside was dimmed with the
      paragraph and measured 3.17:1. A colour alpha only affects text that does
      not set its own colour, so the link keeps full strength.
    */}
    {/*
      The ask is feedback, not a star. On a tool whose scoring rules *are* the
      product, someone telling us a rule is wrong is worth more than a metric on
      a page they will never revisit.
    */}
    <p className="text-base-content/70">
      This project is free and ad-light. If it helped you,{' '}
      <Link to="/contact" className="link link-primary">
        telling us what worked and what did not
      </Link>{' '}
      is the most useful thing you can do.
    </p>
  </aside>
)

export const Layout = ({ children, wide = false }: { children: ReactNode; wide?: boolean }) => (
  <div className="flex min-h-dvh flex-col bg-base-200">
    <a href="#main" className="skip-link">
      Skip to main content
    </a>
    <Header />
    {/*
      `tabIndex={-1}` makes this focusable programmatically without adding it to
      the tab order. Two things depend on it, and neither worked without it:
      the skip link — which was moving the viewport but leaving focus in the
      header, so the next Tab went back to the navigation the user had just
      asked to skip — and the route-change handler, which focuses this landmark
      so a new page announces itself. An element with no tabIndex silently
      ignores `.focus()`, which is why both failures were invisible.
    */}
    <main
      id="main"
      tabIndex={-1}
      className={`flex-1 outline-none ${wide ? 'w-full' : 'mx-auto w-full max-w-5xl px-4 py-8'}`}
    >
      {children}
    </main>
    <Footer />
    {/* Renders nothing unless advertising is configured and unanswered. On a
        default deployment this mounts and immediately returns null. */}
    <ConsentBanner />
    {/* The corner promo toast (PromoNotification) was removed on 2026-10-19:
        it appeared over the tool pages while people were checking a resume.
        The same two links stay in the footer and the header menu, which only
        show when someone looks for them. */}
    {/* One instance for the whole app — the homepage banner and the footer
        link above both just call `openShare` on the shared store. */}
    <ShareModal />
    {/* One instance for the whole app — the resume export flow and the
        end-of-guide CTA both just call `openSharePrompt` on their own store.
        Its "Share Now" hands off to the ShareModal above. */}
    <SharePromptModal />
  </div>
)
